// Types
import { OrderType } from '../types/orders';
import type { FormContext } from '../offline/formStorage';

// Constants
import {
  directoryNaming,
  formAccidentNaming,
} from '../components/forms/AddBasicInfoAccidentForm/constants';

// Utils
import { decompressSessionStorage } from './sessionStorage';
import { compareImageJSONs, compareSketchJson } from './image';
import { draftSketchFile, draftSketchFileName } from './sketchFile';
import {
  ACCIDENT_EDIT_DRAFT_KEYS,
  ACCIDENT_EDIT_SCOPE,
  ACCIDENT_MEDIA_DRAFT_KEYS,
  ACCIDENT_MEDIA_SCOPE,
} from './draftScopes';

/**
 * Contexto de navegación de un accidente (`accident-navigation-context`):
 * de dónde se vino, para la miga y para saber a dónde volver tras guardar.
 *
 * Desde el 2026-11-02 las dos puertas de EDITAR (el lápiz de la tarjeta
 * `AccidentOT` y el «Editar» de `AccidentsPage`) escriben `entityId` = la
 * Order del accidente que abren. Al llegar a la edición se comprueba como un
 * borrador más (spec wizard-draft-ownership § 6, «Contextos de navegación»):
 * un contexto de OTRO accidente (o sin `entityId`, escrito por otra puerta o
 * antes de este cambio) se descarta, y el destino de vuelta sale del padre
 * que da el servidor.
 */
export type AccidentNavigationContext = {
  entityId?: string;
  origin?: string;
  returnTo?: string;
  parentOrderId?: string;
  parentRoid?: string;
  parentOrderType?: string;
  entityRoid?: string;
  [key: string]: unknown;
};

type ReadableStore = Pick<Storage, 'getItem'>;

/** El contexto guardado, o `null` si no hay o no se puede leer. Nunca lanza. */
export const readAccidentNavigationContext = (
  store: ReadableStore = sessionStorage
): AccidentNavigationContext | null => {
  let raw: string | null;
  try {
    raw = store.getItem(formAccidentNaming.ACCIDENT_NAVIGATION_CONTEXT);
  } catch {
    return null;
  }
  if (!raw) return null;
  try {
    const parsed = JSON.parse(decompressSessionStorage(raw) ?? 'null');
    return parsed && typeof parsed === 'object' && !Array.isArray(parsed)
      ? (parsed as AccidentNavigationContext)
      : null;
  } catch {
    return null;
  }
};

/** ¿Es este contexto del accidente `entityId`? Solo si lo dice su `entityId`. */
export const navigationContextBelongsTo = (
  context: AccidentNavigationContext | null,
  entityId: string | undefined
): context is AccidentNavigationContext =>
  !!context && !!entityId && context.entityId === entityId;

/**
 * El contexto de navegación SOLO si es del accidente de la URL; si es de otro
 * (o no dice de cuál es), `null`: esta edición lo IGNORA — ni la miga pinta su
 * OT ni el guardado vuelve a ella (R5: con atrás, la edición de A heredaba el
 * contexto que dejó el lápiz de B).
 *
 * No lo borra: la misma clave la lee el detalle del accidente para su
 * «Volver» (`AccidentPageSummary`), y volver de una URL de edición al detalle
 * de otro accidente no debe dejarlo sin destino (revisión de la Task 7). Sin
 * contexto propio, el destino de vuelta sale del padre que da el servidor
 * (`resolveAccidentEditRedirect`).
 */
export const readOwnAccidentNavigationContext = (
  urlEntityId: string | undefined,
  store: ReadableStore = sessionStorage
): AccidentNavigationContext | null => {
  const context = readAccidentNavigationContext(store);
  return navigationContextBelongsTo(context, urlEntityId) ? context : null;
};

/**
 * La copia de un paso de la edición (`key`), ya parseada, SOLO si es de la
 * Order `orderId`. Cada copia lo dice: el paso 1 y el 2 llevan `orderId` (del
 * servidor), el 3 `orderParentId` (se lo pone su loader). Ilegible, sin id o
 * de otra Order → `null`, y el loader carga del servidor.
 *
 * Tras `claimDraftScope(ACCIDENT_EDIT_SCOPE, :id)` las copias ya son de la URL;
 * esto es el cinturón: los pasos 2 y 3 sacaban la Order de la copia del paso 1
 * sin mirar la URL (auditoría § 0, `BasicInfo/loader.ts:53`,
 * `DamagesAndVehicles/loader.ts:35`) y ahora la sacan de `:id`.
 */
export const readAccidentEditCopy = <T extends object = Record<string, unknown>>(
  key: string,
  orderId: string | undefined,
  store: ReadableStore = sessionStorage
): T | null => {
  if (!orderId) return null;
  let raw: string | null;
  try {
    raw = store.getItem(key);
  } catch {
    return null;
  }
  if (!raw) return null;
  try {
    const copy = JSON.parse(decompressSessionStorage(raw) ?? 'null');
    if (!copy || typeof copy !== 'object' || Array.isArray(copy)) return null;
    const copyOrderId = copy.orderId ?? copy.orderParentId;
    return copyOrderId === orderId ? (copy as T) : null;
  } catch {
    return null;
  }
};

/** El padre del accidente según el servidor (lo trae el loader del paso 1). */
export type AccidentServerParent = {
  id?: string | null;
  type?: string | null;
};

/**
 * A dónde volver tras guardar la edición del accidente `orderId`.
 *
 * - Contexto del MISMO accidente: como siempre — a la campaña o a la OT de la
 *   que se vino; sin padre en el contexto (la tabla de Accidentes), al
 *   detalle del accidente.
 * - Contexto de otro accidente, sin `entityId` o ausente: al padre que da el
 *   servidor (mismo criterio que `parentOrderIdFromData` en el resumen de
 *   actuación). Sin padre conocido, al detalle del accidente.
 *
 * Rutas absolutas para el padre: las relativas con `../../../../` rompen en
 * `/work-orders/campaign/:cid/accidents/edit-accident/...` (6 niveles).
 */
export const resolveAccidentEditRedirect = ({
  orderId,
  navigationContext,
  serverParent,
}: {
  orderId: string;
  navigationContext: AccidentNavigationContext | null;
  serverParent?: AccidentServerParent | null;
}): string => {
  const fallback = `../../../../accidents/${orderId}`;

  if (navigationContextBelongsTo(navigationContext, orderId)) {
    const isCampaign =
      navigationContext.parentOrderType === OrderType.Campaign ||
      navigationContext.parentRoid?.startsWith('Ca');
    if (isCampaign && navigationContext.parentOrderId) {
      return `/work-orders/campaign/${navigationContext.parentOrderId}`;
    }
    if (navigationContext.parentOrderId) {
      return `/work-orders/${navigationContext.parentOrderId}`;
    }
    return fallback;
  }

  if (serverParent?.id) {
    return serverParent.type === OrderType.Campaign
      ? `/work-orders/campaign/${serverParent.id}`
      : `/work-orders/${serverParent.id}`;
  }
  return fallback;
};

/**
 * Nombres de las fotos NUEVAS de la edición en curso (las que lista la copia
 * del paso 2 y no estaban en el servidor). Son las que el guardado entrega a
 * la bandeja; nunca lanza.
 */
const newPhotoNamesOfDraft = (store: ReadableStore): string[] => {
  try {
    const raw = store.getItem(formAccidentNaming.EDIT_BASIC_INFO_ACCIDENT_FORM);
    if (!raw) return [];
    const draft = JSON.parse(decompressSessionStorage(raw) ?? 'null');
    const { imagesToSave } = compareImageJSONs(
      draft?.dataToCompare?.images,
      draft?.images
    );
    return imagesToSave
      .map((image: { name?: unknown }) => image?.name)
      .filter((name: unknown): name is string => typeof name === 'string');
  } catch {
    return [];
  }
};

/**
 * El croquis de la edición en curso si la edición lo CAMBIA (copia del paso 2
 * frente al servidor): el campo `sketch` de la copia. `undefined` si no lo
 * cambia o no se puede leer. Nunca lanza.
 */
const changedSketchOfEdit = (store: ReadableStore): unknown => {
  try {
    const raw = store.getItem(formAccidentNaming.EDIT_BASIC_INFO_ACCIDENT_FORM);
    if (!raw) return undefined;
    const draft = JSON.parse(decompressSessionStorage(raw) ?? 'null');
    return compareSketchJson(draft?.sketch ?? null, draft?.dataToCompare?.sketch ?? null)
      .sketchToSaveOrUpdate
      ? draft.sketch
      : undefined;
  } catch {
    return undefined;
  }
};

/**
 * Nombre del fichero de croquis que se lleva un guardado de la edición: el que
 * apunta su borrador (`fileName`, Ruling 15), el que sube `UploadImage` con
 * `canvasFileName`, el que copia el rescate sin red y el que subirá el
 * reintento desde la copia de la cola. Sin marca y con dibujo local (borrador
 * o operación de antes del cambio), el PRIMERO de `canvas-accident`, lo de
 * siempre (`draftSketchFileName`). `null` si no hay o no se puede mirar.
 *
 * Hace falta saberlo para subir el suyo y no «el primero de la carpeta» (que
 * comparten las pestañas y crear y editar accidente) y para borrar después SOLO
 * ese fichero, por NOMBRE, nunca la carpeta (Ruling 5).
 */
export const ownSketchFileName = async (
  sketchField: unknown,
  images: Pick<FormContext['images'], 'list'>
): Promise<string | null> =>
  draftSketchFileName(draftSketchFile(sketchField), images, directoryNaming.CANVAS);

/**
 * El fichero de croquis de la edición en curso, si la edición cambia el
 * croquis (si no, el que haya en la carpeta no es suyo). Para la rama que
 * encola: lo que acaba de capturar la operación.
 */
export const sketchFileOfEdit = async (form: FormContext): Promise<string | null> => {
  const sketch = changedSketchOfEdit(form.store);
  return sketch === undefined ? null : ownSketchFileName(sketch, form.images);
};

/**
 * Cierra el borrador de la edición del accidente `orderId` tras guardarlo (o
 * encolarlo): borra SOLO lo suyo.
 *
 * Antes se llamaba a `removeNewAccidentStorage`, que además tiraba el alta de
 * accidente a medias de la pestaña (`new-accident-form`, sus datos básicos y
 * vehículos) y vaciaba las carpetas compartidas `accident`/`canvas-accident`
 * — las fotos sin guardar de esa alta y de cualquier otra pestaña (auditoría
 * de accidentes § 6, riesgos). Ahora:
 *
 * - Las copias de los tres pasos y su dueño (`ACCIDENT_EDIT_SCOPE`).
 * - El croquis en curso y la lista de ficheros (`ACCIDENT_MEDIA_SCOPE`), solo
 *   si su dueño es este accidente: si no, son de otro asistente.
 * - De la carpeta `accident`, solo las fotos NUEVAS que listaba esta edición:
 *   ya están en la bandeja (`offline-pending-uploads`) o en la operación
 *   encolada, y dejarlas haría que una foto con el mismo nombre en otra
 *   edición no se pudiera escribir (OPFS no pisa un fichero que existe).
 * - De `canvas-accident`, solo el croquis que se llevó este guardado
 *   (`sketchFileName`, ver `ownSketchFileName`): ya va en la imagen subida o
 *   en la operación encolada, y dejarlo sería un huérfano en la carpeta
 *   compartida.
 *
 * El contexto de navegación NO se toca: decide a dónde se vuelve.
 */
export const releaseAccidentEditDraft = async (
  form: FormContext,
  orderId: string | undefined,
  { sketchFileName = null }: { sketchFileName?: string | null } = {}
): Promise<void> => {
  const photoNames = newPhotoNamesOfDraft(form.store);

  ACCIDENT_EDIT_DRAFT_KEYS.forEach((key) => form.store.removeItem(key));
  form.store.removeItem(ACCIDENT_EDIT_SCOPE.ownerKey);

  if (orderId && form.store.getItem(ACCIDENT_MEDIA_SCOPE.ownerKey) === orderId) {
    ACCIDENT_MEDIA_DRAFT_KEYS.forEach((key) => form.store.removeItem(key));
    form.store.removeItem(ACCIDENT_MEDIA_SCOPE.ownerKey);
  }

  await Promise.all([
    ...photoNames.map((name) =>
      form.images.remove(OrderType.Accident, name).catch(() => undefined)
    ),
    ...(sketchFileName
      ? [form.images.remove(directoryNaming.CANVAS, sketchFileName).catch(() => undefined)]
      : []),
  ]);
};
